"use client";

import { useEffect, useRef, useSyncExternalStore } from "react";
import type { createExternalSetStore } from "@/utils/query";

type ExternalSetStore = ReturnType<typeof createExternalSetStore>;

/**
 * Binds a React Query hook to an external invalidation set-store.
 *
 * Reads `isInvalidating` (true while `key` is in the store) synchronously via
 * `useSyncExternalStore`, and removes `key` from the store once the associated
 * query settles — specifically, on the transition from fetching → not-fetching
 * while invalidation is active. That transition detection is what keeps the
 * loader visible across the full re-fetch triggered by `invalidateQueries`.
 *
 * All state mutation happens inside a `useEffect`, never during render.
 * Consumers pass `isFetching` in from `useQuery` and just read back
 * `isInvalidating`.
 */
export function useExternalInvalidation(store: ExternalSetStore, key: string, isFetching: boolean) {
  const isInvalidating = useSyncExternalStore(store.subscribe, () => store.has(key));
  const prevIsFetchingRef = useRef(isFetching);

  useEffect(() => {
    const wasJustFetching = prevIsFetchingRef.current && !isFetching;
    prevIsFetchingRef.current = isFetching;
    if (isInvalidating && wasJustFetching) {
      store.delete(key);
    }
  }, [isInvalidating, isFetching, key, store]);

  return { isInvalidating };
}
